import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useNextScheduledLesson from '@/data/hook/student-dashboard/useNextScheduledLesson';
import useStudentExamPendency from '@/data/hook/student-dashboard/useStudentExamPendency';
import useListService from '@/data/hook/useListService';
import { lessonsProgressesQueryKeys } from '@/data/services/querykeys';
import { isHomeworkPending } from '@/utils/homework-progress/isHomeworkPending';

type UseStudentPendenciesParams = {
  studentId?: number;
  klassId?: number;
  courseProgressId?: number;
};

export default function useStudentPendencies({
  studentId,
  klassId,
  courseProgressId,
}: UseStudentPendenciesParams) {
  const {
    examPendency,
    isLoading: isLoadingRecords,
    error: recordsError,
  } = useStudentExamPendency({ studentId, klassId });

  const { nextLesson, isLoading: isLoadingNextLesson } = useNextScheduledLesson(
    { klassId },
  );

  const nextLessonId = nextLesson?.lesson?.id;
  const hasNextLessonHomework = !!nextLesson?.homeworkActivity;

  const {
    results: lessonProgresses,
    isInitialLoading: isLoadingLessonProgress,
    error: lessonProgressError,
  } = useListService({
    ...lessonsProgressesQueryKeys.list({
      courseProgressId: courseProgressId ? [courseProgressId] : undefined,
      lesson: nextLessonId ? [nextLessonId] : undefined,
    }),
    enabled: !!courseProgressId && !!nextLessonId && hasNextLessonHomework,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const homeworkCount = isHomeworkPending(
    lessonProgresses.at(0)?.homeworkProgress,
  )
    ? 1
    : 0;
  const nextLessonDate = nextLesson?.datetime;

  const hasPendencies =
    examPendency.count > 0 || homeworkCount > 0 || !!nextLessonDate;

  return {
    examPendency,
    homeworkCount,
    nextLessonDate,
    hasPendencies,
    isLoading:
      isLoadingRecords || isLoadingNextLesson || isLoadingLessonProgress,
    error: recordsError ?? lessonProgressError,
  };
}
